/**
 * Sanitizes a DateTimeOffset string to extract just the datetime portion
 * Server sends times already in the event's timezone (e.g., "2026-01-13T13:00:00-05:00")
 * This extracts "2026-01-13T13:00"
 */
export function sanitizeDateTimeInput(isoDateTime) {
    if (!isoDateTime)
        return '';
    let core = isoDateTime.trim();
    if (core.endsWith('Z'))
        core = core.slice(0, -1);
    if (/\.\d{3}$/.test(core))
        core = core.replace(/\.\d{3}$/, '');
    if (core.length >= 16 && core.includes('T'))
        core = core.slice(0, 16);
    return core;
}
/**
 * Formats a DateOnly string as "Month Nth, Year" (e.g., "January 1st, 2026")
 * @param dateOnly - ISO 8601 date string (e.g., "2026-01-01")
 * @returns Formatted date string with ordinal suffix
 */
export function formatDateOnly(dateOnly) {
    if (!dateOnly)
        return '—';
    try {
        const [year, month, day] = dateOnly.split('-');
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[parseInt(month, 10) - 1];
        const dayNum = parseInt(day, 10);
        // Add ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
        const getOrdinalSuffix = (n) => {
            const lastDigit = n % 10;
            const lastTwoDigits = n % 100;
            if (lastTwoDigits >= 11 && lastTwoDigits <= 13)
                return 'th';
            if (lastDigit === 1)
                return 'st';
            if (lastDigit === 2)
                return 'nd';
            if (lastDigit === 3)
                return 'rd';
            return 'th';
        };
        return `${monthName} ${dayNum}${getOrdinalSuffix(dayNum)}, ${year}`;
    }
    catch {
        return '—';
    }
}
/**
 * Formats event datetime for display
 * @param isoDateTime - DateTimeOffset string from server (already in event's timezone)
 * @param options - Formatting options
 * @returns Formatted date/time string
 */
export function formatEventDateTime(isoDateTime, options = { includeDate: true, includeTime: true }) {
    const sanitized = sanitizeDateTimeInput(isoDateTime);
    if (!sanitized)
        return '—';
    const [datePart, timePart] = sanitized.split('T');
    const [year, month, day] = datePart.split('-');
    const [hour, minute] = timePart.split(':');
    const hourNum = parseInt(hour, 10);
    const isPM = hourNum >= 12;
    const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    const parts = [];
    if (options.includeDate !== false) {
        parts.push(`${month}/${day}/${year}`);
    }
    if (options.includeTime !== false) {
        const timePart = `${displayHour}:${minute} ${isPM ? 'PM' : 'AM'}`;
        parts.push(timePart);
    }
    let result = parts.join(', ');
    if (options.timezoneName) {
        result += ` ${options.timezoneName}`;
    }
    return result;
}
/**
 * Formats a date range for display (e.g., "January 13 at 1:00 PM - 3:00 PM ET")
 */
export function formatEventDateRange(startDateTime, endDateTime, timezoneName) {
    if (!startDateTime || !endDateTime)
        return '—';
    const startSanitized = sanitizeDateTimeInput(startDateTime);
    const endSanitized = sanitizeDateTimeInput(endDateTime);
    if (!startSanitized || !endSanitized)
        return '—';
    const [startDate, startTime] = startSanitized.split('T');
    const [endDate, endTime] = endSanitized.split('T');
    const [, startMonth, startDay] = startDate.split('-');
    const [, endMonth, endDay] = endDate.split('-');
    const sameDay = startDate === endDate;
    // Format start time
    const [startHour, startMinute] = startTime.split(':');
    const startHourNum = parseInt(startHour, 10);
    const startIsPM = startHourNum >= 12;
    const startDisplayHour = startHourNum === 0 ? 12 : startHourNum > 12 ? startHourNum - 12 : startHourNum;
    const startTimeFormatted = `${startDisplayHour}:${startMinute} ${startIsPM ? 'PM' : 'AM'}`;
    // Format end time
    const [endHour, endMinute] = endTime.split(':');
    const endHourNum = parseInt(endHour, 10);
    const endIsPM = endHourNum >= 12;
    const endDisplayHour = endHourNum === 0 ? 12 : endHourNum > 12 ? endHourNum - 12 : endHourNum;
    const endTimeFormatted = `${endDisplayHour}:${endMinute} ${endIsPM ? 'PM' : 'AM'}`;
    // Format date part
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    const startMonthName = monthNames[parseInt(startMonth, 10) - 1];
    const startDateFormatted = `${startMonthName} ${parseInt(startDay, 10)}`;
    let result;
    if (sameDay) {
        result = `${startDateFormatted} at ${startTimeFormatted} - ${endTimeFormatted}`;
    }
    else {
        const endMonthName = monthNames[parseInt(endMonth, 10) - 1];
        const endDateFormatted = `${endMonthName} ${parseInt(endDay, 10)}`;
        result = `${startDateFormatted} ${startTimeFormatted} - ${endDateFormatted} ${endTimeFormatted}`;
    }
    if (timezoneName) {
        result += ` ${timezoneName}`;
    }
    return result;
}
